import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse } from "../_app/api";
import { getUsageAnalysis, getUsageAnalysisColumns, getUsageAnalysisSummary, getUsageAnalysisSummaryTotal } from "./api";
import { Query } from "../filter/types";
import { UsageAnalysisResponse, UsageAnalysisSummaryTable } from "./types";
import { SortParam } from "../_app/components/Table/helpers";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useGetUsageAnalysisColumns(tableType: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usageAnalysisColumns", { accountId, tableType }],
    queryFn: () => getUsageAnalysisColumns(accountId, tableType),
    ...options,
  });
}

export function useGetUsageAnalysis(limit: number, sort: SortParam[], queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<UsageAnalysisResponse, AjaxResponse>({
    queryKey: ["usageAnalysis", { accountId, limit, sort, queries }],
    queryFn: ({ pageParam }) => getUsageAnalysis(accountId, pageParam as string, limit, sort, queries),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    retry: 2,
    ...options,
  });
}

export function useGetUsageAnalysisSummary(limit: number, sort: SortParam[], type: string, queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<UsageAnalysisSummaryTable, AjaxResponse>({
    queryKey: ["usageAnalysisSummary", { accountId, type, limit, sort, queries }],
    queryFn: ({ pageParam }) => getUsageAnalysisSummary(accountId, pageParam as string, limit, sort, type, queries),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    retry: 2,
    ...options,
  });
}

export function useGetUsageAnalysisSummaryTotal(queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usageAnalysisSummaryTotal", { accountId, queries }],
    queryFn: () => getUsageAnalysisSummaryTotal(accountId, queries),
    retry: 2,
    ...options,
  });
}

export function useGetUsageAnalysisSupportedColumns(tableType: string) {
  const { data: supportedColsAll, isFetching: isFetchingAll } = useGetUsageAnalysisColumns("ALL", {
    enabled: tableType === "ALL",
  });
  const { data: supportedColsCli, isFetching: isFetchingCli } = useGetUsageAnalysisColumns("CLI", {
    enabled: tableType === "CLI",
  });
  const { data: supportedColsAccount, isFetching: isFetchingAccount } = useGetUsageAnalysisColumns("ACCOUNT", {
    enabled: tableType === "ACCOUNT",
  });
  const { data: supportedColsCostCentre, isFetching: isFetchingCC } = useGetUsageAnalysisColumns("COST_CENTRE", {
    enabled: tableType === "COST_CENTRE",
  });

  const isFetching = isFetchingAll || isFetchingCli || isFetchingAccount || isFetchingCC;

  return {
    data: {
      ALL: supportedColsAll,
      CLI: supportedColsCli,
      ACCOUNT: supportedColsAccount,
      COST_CENTRE: supportedColsCostCentre,
    },
    isFetching,
  };
}
